import React from 'react';
import { EyeIcon, PencilIcon, TrashIcon, PhoneIcon, MapPinIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import Table from '../common/Table';
import StatusBadge from '../common/StatusBadge';

/**
 * Business Profiles Table Component
 * 
 * @param {Object} props
 * @param {Array} props.data - Business profiles data
 * @param {Function} props.onView - View handler
 * @param {Function} props.onEdit - Edit handler
 * @param {Function} props.onDelete - Delete handler
 */
const BusinessProfilesTable = ({
  data = [],
  onView,
  onEdit,
  onDelete,
  pagination,
  onPageChange,
  header,
  isLoading = false,
}) => {
  const getBusinessTypeColor = (type) => {
    const colors = {
      retail: 'bg-blue-100 text-blue-800',
      service: 'bg-purple-100 text-purple-800',
      manufacturing: 'bg-yellow-100 text-yellow-800',
      others: 'bg-gray-100 text-gray-800',
    };
    return colors[typeof type === 'string' ? type.toLowerCase() : 'others'] || 'bg-gray-100 text-gray-800';
  };

  const getClassificationColor = (classification) => {
    const colors = {
      micro: 'bg-green-100 text-green-800',
      small: 'bg-blue-100 text-blue-800',
      medium: 'bg-orange-100 text-orange-800',
      large: 'bg-red-100 text-red-800',
    };
    return colors[typeof classification === 'string' ? classification.toLowerCase() : 'others'] || 'bg-gray-100 text-gray-800';
  };

  const columns = [
    {
      key: 'business',
      label: 'Business Information',
      render: (_, row) => (
        <div className="flex items-center">
          <div className="flex-shrink-0 h-12 w-12 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm hover:scale-110 transition-transform duration-200">
            {row.business_name?.charAt(0) || 'B'}
          </div>
          <div className="ml-4 flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-900 truncate">
              {row.business_name || 'N/A'}
            </div>
            <div className="text-xs text-gray-500 truncate mt-1">
              {row.business_code || 'N/A'}
            </div>
            <div className="flex items-center space-x-2 mt-1">
              <span className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded ${getBusinessTypeColor(row.business_type?.name || row.business_type)}`}>
                {row.business_type?.name || row.business_type || 'N/A'}
              </span>
              <span className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded ${getClassificationColor(row.classification?.name || row.classification)}`}>
                {row.classification?.name || row.classification || 'N/A'}
              </span>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'owner',
      label: 'Owner Information',
      render: (_, row) => (
        <div className="text-sm text-gray-900">
          <div className="font-medium text-gray-900 truncate">
            {row.owner_name || 'N/A'}
          </div>
          {/* <div className="flex items-center text-xs mt-1">
            {row.contact_number && (
              <>
                <PhoneIcon className="h-3 w-3 text-gray-400 mr-1" />
                <span className="truncate">{row.contact_number}</span>
              </>
            )}
          </div> */}
        </div>
      ),
    },
    {
      key: 'location',
      label: 'Location',
      render: (_, row) => (
        <div className="text-sm text-gray-900">
          <div className="flex items-center text-xs">
            <MapPinIcon className="h-3 w-3 text-gray-400 mr-1" />
            <span className="truncate max-w-xs gap-1">
              {row.full_address || 'N/A'},
            </span>
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <StatusBadge status={value} />
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, row) => (
        <div className="flex items-center justify-end space-x-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onView && onView(row);
            }}
            className="text-blue-600 hover:text-blue-900 p-1.5 rounded-full hover:bg-blue-50 transition-all duration-200 hover:scale-110"
            title="View Details"
          >
            <EyeIcon className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit && onEdit(row);
            }}
            className="text-yellow-600 hover:text-yellow-900 p-1.5 rounded-full hover:bg-yellow-50 transition-all duration-200 hover:scale-110"
            title="Edit"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete && onDelete(row);
            }}
            className="text-red-600 hover:text-red-900 p-1.5 rounded-full hover:bg-red-50 transition-all duration-200 hover:scale-110"
            title="Delete"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      data={data}
      getRowKey={(row) => row.id}
      header={header}
      pagination={pagination}
      onPageChange={onPageChange}
      paginationProps={{
        isLoading,
        showSizeSelector: true,
      }}
    />
  );
};

export default BusinessProfilesTable;
